import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { VideoCamera, MapPin, CalendarBlank, Trash } from "@phosphor-icons/react";

export function LiveSessionsAdmin({ staff }) {
  const [sessions, setSessions] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "New-hire orientation (live)", date: "", start: "", end: "", mode: "in-person", location: "", zoom_link: "", facilitator: "Kelly Schmunk", attendee_ids: [], notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/live-sessions").then(({ data }) => setSessions(data.sessions || [])).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Title and date are required"); return; }
    if (form.mode === "zoom" && !form.zoom_link.trim()) { toast.error("Paste the Zoom link"); return; }
    setBusy(true);
    try {
      await api.post("/admin/live-sessions", form);
      toast.success("Session scheduled");
      setShowForm(false);
      setForm({ title: "New-hire orientation (live)", date: "", start: "", end: "", mode: "in-person", location: "", zoom_link: "", facilitator: "Kelly Schmunk", attendee_ids: [], notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const mark = async (sid, uid, attended) => {
    try {
      await api.post(`/admin/live-sessions/${sid}/attendance`, { user_id: uid, attended });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="live-sessions-admin">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Live sessions — guided orientation</p>
          <p className="mt-0.5 text-xs text-stone-500">Facilitator-led, in-person or Zoom. Attendees see it on their dashboard.</p>
        </div>
        <Button size="sm" className="bg-primary text-primary-foreground hover:bg-stone-900" onClick={() => setShowForm((v) => !v)} data-testid="live-session-new-btn">
          {showForm ? "Cancel" : "Schedule a session"}
        </Button>
      </div>

      {showForm && (
        <div className="mt-4 space-y-2 border-t border-stone-100 pt-4" data-testid="live-session-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Session title"
            className="w-full rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-title" />
          <div className="grid gap-2 sm:grid-cols-3">
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-date" />
            <input value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} placeholder="Start (e.g. 9:00 AM)"
              className="rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-start" />
            <input value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} placeholder="End (e.g. 12:00 PM)"
              className="rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-end" />
          </div>
          <div className="flex gap-2">
            {[["in-person", "In person"], ["zoom", "Zoom"]].map(([v, label]) => (
              <button key={v} onClick={() => setForm({ ...form, mode: v })}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${form.mode === v ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}
                data-testid={`live-session-mode-${v}`}>{label}</button>
            ))}
          </div>
          {form.mode === "in-person" ? (
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (office address, room…)"
              className="w-full rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-location" />
          ) : (
            <input value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} placeholder="Paste the Zoom meeting link"
              className="w-full rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-zoom" />
          )}
          <input value={form.facilitator} onChange={(e) => setForm({ ...form, facilitator: e.target.value })} placeholder="Facilitator"
            className="w-full rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="live-session-facilitator" />
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone-500">Attendees</p>
            <div className="max-h-36 space-y-1 overflow-y-auto rounded-lg border border-stone-200 p-2" data-testid="live-session-attendees">
              {(staff || []).map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-xs text-stone-700">
                  <input type="checkbox" checked={form.attendee_ids.includes(s.id)}
                    onChange={(e) => setForm({ ...form, attendee_ids: e.target.checked ? [...form.attendee_ids, s.id] : form.attendee_ids.filter((x) => x !== s.id) })}
                    className="accent-[#6a3e97]" data-testid={`live-session-attendee-${s.id}`} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={create} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="live-session-save">
            {busy ? "Saving…" : "Schedule session"}
          </Button>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {(sessions || []).map((s) => (
          <div key={s.id} className="rounded-lg border border-stone-200 p-3" data-testid={`live-session-${s.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-stone-800">{s.title}</p>
                <p className="mt-0.5 flex items-center gap-2 text-xs text-stone-500">
                  <CalendarBlank size={12} /> {s.date}{s.start ? ` · ${s.start}–${s.end}` : ""}
                  {s.mode === "zoom" ? <span className="inline-flex items-center gap-1"><VideoCamera size={12} /> Zoom</span> : <span className="inline-flex items-center gap-1"><MapPin size={12} /> {s.location || "In person"}</span>}
                  {s.facilitator ? ` · led by ${s.facilitator}` : ""}
                </p>
              </div>
              <button onClick={async () => { if (window.confirm("Delete this session?")) { await api.delete(`/admin/live-sessions/${s.id}`); load(); } }}
                className="text-stone-300 hover:text-[#C0392B]" data-testid={`live-session-delete-${s.id}`}><Trash size={14} /></button>
            </div>
            {(s.attendees || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5 border-t border-stone-100 pt-2">
                {s.attendees.map((a) => (
                  <button key={a.id} onClick={() => mark(s.id, a.id, !a.attended)} title="Click to toggle attendance"
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${a.attended ? "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" : "border-stone-200 bg-stone-50 text-stone-500"}`}
                    data-testid={`live-session-attend-${s.id}-${a.id}`}>
                    {a.attended ? "✓ " : ""}{a.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sessions && sessions.length === 0 && <p className="text-xs text-stone-400" data-testid="live-sessions-empty">No sessions scheduled yet.</p>}
      </div>
    </div>
  );
}

export function MyLiveSessions() {
  const [sessions, setSessions] = useState(null);
  useEffect(() => { api.get("/my/live-sessions").then(({ data }) => setSessions(data.sessions || [])).catch(() => setSessions([])); }, []);
  if (!sessions || sessions.length === 0) return null;
  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-primary/30 bg-white shadow-sm" data-testid="my-live-sessions">
      <div className="border-b border-stone-100 px-4 py-3">
        <p className="font-heading text-sm font-semibold text-stone-900">Your scheduled live orientation</p>
      </div>
      <div className="divide-y divide-stone-50">
        {sessions.map((s) => (
          <div key={s.id} className="px-4 py-3" data-testid={`my-live-session-${s.id}`}>
            <p className="text-sm font-medium text-stone-800">{s.title}</p>
            <p className="mt-0.5 text-xs text-stone-500">
              {s.date}{s.start ? ` · ${s.start}–${s.end}` : ""} · {s.mode === "zoom" ? "on Zoom" : (s.location || "in person")}{s.facilitator ? ` · led by ${s.facilitator}` : ""}
            </p>
            {s.mode === "zoom" && s.zoom_link && (
              <a href={s.zoom_link} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground hover:bg-stone-900"
                data-testid={`my-live-join-${s.id}`}><VideoCamera size={13} /> Join the Zoom session</a>
            )}
            {s.notes && <p className="mt-1 text-xs text-stone-400">{s.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
